import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import addedLeadIcon from 'assets/salesReports/addedLead.svg';
import box from 'assets/salesReports/box.svg';
import conversionRateIcon from 'assets/salesReports/conversionRate.svg';
import convertedIcon from 'assets/salesReports/converted.svg';
import leadIcon from 'assets/salesReports/lead.svg';
import lostLeads from 'assets/salesReports/lostLeads.svg';
import Button from 'common-ui/button';
import CustomTable from 'common-ui/table/Table';
import loadIcon from 'common-ui/table/loading.svg';
import {
  getSalesFunnel,
  getSalesLeads,
  getSalesOverview,
  getSalesProjects,
} from 'features/sales/salesActions';
import {
  selectSalesLeads,
  selectSalesLeadsLoading,
  selectSalesOverview,
  selectSalesOverviewLoading,
  selectSalesProjects,
  selectSalesProjectsLoading,
} from 'features/sales/salesSlice';

import {
  Container,
  DashboardGrid,
  FunnelBox,
  FunnelCardGrid,
  FunnelWrapper,
  GradientText,
  Header,
  HeaderTitle,
  Icon,
  IconWrapper,
  LoadContainer,
  LoadingIcon,
  MostSold,
  MostSoledOffersHeader,
  TableRankIndex,
} from './SalesReport.styles';
import Funnel, { FunnelCard } from './components/funnel';
import HorizontalCard from './components/horizontalCard';
import MetricCard from './components/metricCard';
import TotalRevenue from './components/totalRevenue';
import {
  SalesConversionChart,
  SalesPerformancePieChart,
  TopDepartmentsSalesChart,
} from './diagrams';

const SalesReport = () => {
  const dispatch = useDispatch();
  const projects = useSelector(selectSalesProjects);
  const leads = useSelector(selectSalesLeads);
  const projectsLoading = useSelector(selectSalesProjectsLoading);
  const leadsLoading = useSelector(selectSalesLeadsLoading);
  const salesOverview = useSelector(selectSalesOverview);
  const salesOverviewLoading = useSelector(selectSalesOverviewLoading);

  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalProjects: 0,
    totalLeads: 0,
    totalSubprojects: 0,
    conversionRate: 0,
    convertedLeads: 0,
  });

  const salesTableData = [
    {
      id: 1,
      rank: 1,
      name: 'John Doe',
      leadsHandled: 25,
      converted: 18,
      conversionRate: '72%',
      revenue: '$45,000',
    },
    {
      id: 2,
      rank: 2,
      name: 'Jane Smith',
      leadsHandled: 22,
      converted: 15,
      conversionRate: '68%',
      revenue: '$38,500',
    },
    {
      id: 3,
      rank: 3,
      name: 'Mike Johnson',
      leadsHandled: 20,
      converted: 12,
      conversionRate: '60%',
      revenue: '$32,000',
    },
    {
      id: 4,
      rank: 4,
      name: 'Sarah Wilson',
      leadsHandled: 18,
      converted: 10,
      conversionRate: '56%',
      revenue: '$28,000',
    },
    {
      id: 5,
      rank: 5,
      name: 'David Brown',
      leadsHandled: 16,
      converted: 8,
      conversionRate: '50%',
      revenue: '$22,000',
    },
  ];

  const salesTableColumns = [
    {
      key: 'rank',
      title: 'Rank',
      dataIndex: 'rank',
      sortable: true,
      width: '10%',
      align: 'center',
      render: (rank, _, rowIndex) => <TableRankIndex $index={rowIndex}>{rank}</TableRankIndex>,
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      sortable: true,
      width: '20%',
    },
    {
      key: 'leadsHandled',
      title: 'Leads Handled',
      dataIndex: 'leadsHandled',
      sortable: true,
      width: '15%',
      align: 'center',
    },
    {
      key: 'converted',
      title: 'Converted',
      dataIndex: 'converted',
      sortable: true,
      width: '15%',
      align: 'center',
    },
    {
      key: 'conversionRate',
      title: 'Conversion Rate',
      dataIndex: 'conversionRate',
      sortable: true,
      width: '20%',
      align: 'center',
    },
    {
      key: 'revenue',
      title: 'Revenue',
      dataIndex: 'revenue',
      sortable: true,
      width: '20%',
      align: 'right',
    },
  ];

  // useEffect(() => {
  //   dispatch(getSalesProjects({ query: {} }));
  //   dispatch(getSalesLeads({ query: {} }));
  // }, [dispatch]);

  useEffect(() => {
    dispatch(getSalesOverview({}));
    dispatch(getSalesFunnel());
  }, [dispatch]);

  const isLoading = projectsLoading || leadsLoading || salesOverviewLoading;

  if (isLoading) {
    return (
      <Container>
        <Header>
          <HeaderTitle>Sales Dashboard</HeaderTitle>
        </Header>
        <LoadContainer>
          <LoadingIcon src={loadIcon} alt="Loading..." />
        </LoadContainer>
      </Container>
    );
  }

  return (
    <Container>
      <DashboardGrid>
        <MetricCard
          backgroundColor="linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)"
          icon={leadIcon}
          iconAlt="Projects"
          value={dashboardMetrics.totalProjects}
          label="Total Leads"
          color="#2B7FFF"
        />

        <MetricCard
          backgroundColor="linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)"
          icon={addedLeadIcon}
          iconAlt="Leads"
          value={dashboardMetrics.totalLeads}
          label="Added Leads"
          color="#00C950"
        />

        <MetricCard
          backgroundColor="linear-gradient(135deg, #ECFDF5 0%, #D0FAE5 100%)"
          icon={convertedIcon}
          iconAlt="Subprojects"
          value={dashboardMetrics.totalSubprojects}
          label="Converted Leads"
          color={'#009966'}
        />

        <MetricCard
          backgroundColor="linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)"
          icon={conversionRateIcon}
          iconAlt="Conversion"
          value={`${dashboardMetrics.conversionRate}%`}
          label="Conversion Rate"
          color={'#9810FA'}
        />
        <MetricCard
          backgroundColor="linear-gradient(135deg, #FEF2F2 0%, #FFE2E2 100%)"
          icon={lostLeads}
          iconAlt="Converted"
          value={dashboardMetrics.convertedLeads}
          label="Converted Leads"
          color={'#E7000B'}
        />
      </DashboardGrid>

      <TotalRevenue />
      <FunnelBox>
        <div>
          <GradientText>Lead Status Funnel</GradientText>
        </div>
        <FunnelWrapper>
          <div>
            <Funnel />
            <Funnel />
            <Funnel />
            <Funnel />
            <Funnel />
          </div>
          <FunnelCardGrid>
            <FunnelCard />
            <FunnelCard />
            <FunnelCard />
            <FunnelCard />
          </FunnelCardGrid>
        </FunnelWrapper>
      </FunnelBox>

      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Sales Performance Distribution</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <SalesPerformancePieChart />
      </MostSold>

      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Sales Conversion Funnel</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <SalesConversionChart />
      </MostSold>

      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Top Departments by Sales</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <TopDepartmentsSalesChart />
      </MostSold>

      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Sales Performance</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <CustomTable
          data={salesTableData}
          columns={salesTableColumns}
          loading={false}
          pagination={false}
          hover={true}
          emptyText="No sales data available"
        />
      </MostSold>
      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Sales Performance</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <CustomTable
          data={salesTableData}
          columns={salesTableColumns}
          loading={false}
          pagination={false}
          hover={true}
          emptyText="No sales data available"
        />
      </MostSold>
      <MostSold>
        <MostSoledOffersHeader>
          <div>
            <IconWrapper>
              <Icon src={box} alt={'box'} />
            </IconWrapper>
            <GradientText>Most Sold Offers</GradientText>
          </div>
          <Button>Show Less</Button>
        </MostSoledOffersHeader>
        <div style={{ gap: 10, display: 'flex', flexDirection: 'column' }}>
          {Array.from({ length: 12 }).map((_, index) => (
            <HorizontalCard index={index} key={index} />
          ))}
        </div>
      </MostSold>
    </Container>
  );
};

export default SalesReport;
